import React, { useCallback, useState, useEffect } from 'react';
import { UserAccess, UserHasAccess } from "components/user-access";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { SetTyreCheck } from 'state/actions';
import { TyreCheckService } from "services/TyreCheckService";
import { Tooltip } from "components/tooltip";
import { Button } from "components/buttons";
import { InstantReportButton } from 'components/reporting/InstantReportButton';
import { ShowHide } from "components/layout";
import { Breadcrumbs } from "components/navigation";
import { NotificationInline, NotificationToaster } from "components/notifications";
import { DetailsListing } from 'pages/tyre-checks/view/DetailsListing';
import { TyreCheckSignOffModal } from 'pages/tyre-checks/modals/TyreCheckSignOffModal';
import { IndividualTyreCheck } from 'pages/tyre-checks/view/IndividualTyreCheck';
import { Intent } from "@blueprintjs/core";
import moment from 'moment';

export function TyreChecksListing(props) {
    const [loadingData, setLoadingData] = useState(false);
    const [loading, setLoading] = useState(true);
    const [signOffRequired, setSignOffRequired] = useState(false);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [showDefectSignoffModal, setShowDefectSignoffModal] = useState(false);
    const [hasOutstandingDefects, setHasOutstandingDefects] = useState(false);
    const [permissionDenied, setPermissionDenied] = useState(false);
    const { id } = useParams();
    const requiredAction = ["Fleet:View", "FleetPCN:View", "FleetVIS:View"];
    const canAccessPage = UserHasAccess(requiredAction);
    const route = useSelector(state => state.route.route);
    const title = useSelector(state => state.route.title);
    const routeId = useSelector(state => state.route.id);
    const tyreCheckData = useSelector(state => state.tyreCheck);
    const [generatingReportInstanceId, setGeneratingReportInstanceId] = useState();
    const [reportDownloading, setReportDownloading] = useState(false);

    const dispatch = useDispatch();
    const responseStatusOK = 200;
    const responseStatusNoContent = 204;


    const [showDetailsListing, setShowDetailsListing] = useState(true);
    const [showOrderId, setShowOrderId] = useState(null);
    const [showAssetId, setShowAssetId] = useState(null);
    const [backToSummary, setBackToSummary] = useState(false);
    
    useEffect(() => {
        const clonedBreadcrumbs = [...props.breadcrumbs];

        if (routeId !== null && route !== null) {
            clonedBreadcrumbs[2] = {
                location: `/${route}/${routeId}/tyre-checks`,
                title: title
            };
        } else {
            clonedBreadcrumbs[2] = {
                title: "Tyre Checks"
            };
        }

        setBreadcrumbs(clonedBreadcrumbs);
    }, [route, routeId, title, tyreCheckData?.orders, props.breadcrumbs]);


    useEffect(() => {
        if (!canAccessPage || !id) return;

        const fetchData = async () => {
            setLoadingData(true);
            try {
                const response = await TyreCheckService.getTyreCheck(id);
                if (response.status === responseStatusOK) {
                    dispatch(SetTyreCheck(response.data));
                } else {
                    setPermissionDenied(true);
                }
            } catch (error) {
                handleError(error,"Failed to load data.");
            } finally {
                setLoadingData(false);
                setLoading(false);
            }
        };

        fetchData();
    }, [id, canAccessPage, dispatch]);

    function handleError(error, customMessage) {
        const noAccessCode = 403;
        NotificationToaster.show(Intent.DANGER, customMessage + " Please try again later.");
        if (error?.response?.status === noAccessCode) {
            setPermissionDenied(true);
        }
    }

    const setupPage = useCallback(() => {
        if (!tyreCheckData?.orders) return;

        let unsignedDamageCount = 0;
        let damageCount = 0;

        if (tyreCheckData.orders && tyreCheckData.orders.length > 0) {

            unsignedDamageCount = tyreCheckData.orders.reduce((acc, order) => {
                const damages = order.positions?.flatMap(position =>
                    (position.damages || []).filter(damage => damage.signOffDateTime === null)
                ) || [];
                return acc + damages.length;
            }, 0);
            damageCount = tyreCheckData.orders.reduce((acc, order) => {
                const damages = order.positions?.flatMap(position =>
                    (position.damages || [])
                ) || [];
                return acc + damages.length;
            }, 0);
        }

        const hasUnsignedDamages = unsignedDamageCount > 0;

        setHasOutstandingDefects(hasUnsignedDamages);
        setSignOffRequired(tyreCheckData.signOffDateTime === null && damageCount > 0);
    }, [tyreCheckData]);

    useEffect(() => {
        if (tyreCheckData && tyreCheckData.orders) {
            setupPage();
            setLoadingData(false);
        }
    }, [loadingData, tyreCheckData, setupPage]);

    function signOffNotification() {
        if (!signOffRequired) {

            if (tyreCheckData.signedOffBy) {
                const signOffDate = tyreCheckData.signOffDateTime
                    ? moment(tyreCheckData.signOffDateTime).format("DD/MM/YYYY HH:mm")
                    : "Not recorded";

                return `Tyre check reports signed off by ${tyreCheckData.signedOffBy} on ${signOffDate}`;
            }

            return "No sign off required because there are no tyre defects";

        }

        if (hasOutstandingDefects) {
            return "This tyre check report still has defects to be signed off before the report can be signed off";
        }

        return "This tyre check report still requires sign off";
    }


    const showOrder = (OrderId, assetId) => {
        setShowDetailsListing(false);
        setShowOrderId(OrderId);
        setShowAssetId(assetId);
        setBackToSummary(true);
    };

    const hideOrder = () => {
        setShowDetailsListing(true);
        setShowOrderId(null);
        setBackToSummary(false);
    };

    function signOffAllDefects() {
        // updateSignOffAll
        TyreCheckService.updateSignOffAll(tyreCheckData.jobId).then(
            (result) => {
                if (result.status === responseStatusNoContent) {
                    // Refresh the data
                    setLoadingData(true);
                    TyreCheckService.getTyreCheck(id).then(
                        (response) => {
                            if (response.status === responseStatusOK) {
                                NotificationToaster.show(Intent.SUCCESS, "All checks signed off successfully.");
                                dispatch(SetTyreCheck(response.data));
                            } else {
                                NotificationToaster.show(Intent.DANGER, "Failed to sign off all checks. Please try again later.");
                                setPermissionDenied(true);
                            }
                            setLoadingData(false);
                        },
                        (error) => {                            
                            handleError(error,"Failed to sign off all checks.");
                            setLoadingData(false);
                        }
                    );
                }
            },
            (error) => {               
                handleError(error, "Failed to sign off all checks.");
            }
        );
    }

    function onReportGenerateSummaryReport() {
        setReportDownloading(true);
        const reportRequest = {
            JobId: id
        };

        TyreCheckService.generateTireTaskReportSummaryReport(reportRequest).then((result) => {
            setGeneratingReportInstanceId(result);
        }, () => {
            NotificationToaster.show(Intent.DANGER, 'Failed to download report');
            setGeneratingReportInstanceId(null);
            setReportDownloading(false);
        })
    }

    function onReportGenerateDetailReport() {
        setReportDownloading(true);
        const reportRequest = {
            JobId: id,
            AssetId: showAssetId
        };

        TyreCheckService.generateTireTaskReportDetailReport(reportRequest).then((result) => {
            setGeneratingReportInstanceId(result);
        }, () => {
            NotificationToaster.show(Intent.DANGER, 'Failed to download report');
            setGeneratingReportInstanceId(null);
            setReportDownloading(false);
        })
    }

    function onReportDownloadComplete() {
        setGeneratingReportInstanceId(null);
        setReportDownloading(false);
    }

    return (
        <UserAccess
            perform={requiredAction}
            yes={() => (
                <ShowHide
                    evaluator={permissionDenied}
                    show={
                        <div className="row">
                            <Breadcrumbs items={breadcrumbs} />
                            <NotificationInline
                                allowClose={false}
                                show
                                text="You do not have permission to view this tyre check."
                                intent="danger"
                            ></NotificationInline>
                        </div>
                    }
                    hide={
                        <div className="row">
                            <Breadcrumbs items={breadcrumbs} />
                            <h1>Tyre Checks</h1>
                            <UserAccess
                                perform={["Fleet:View", "FleetPCN:View", "FleetVIS:View"]}
                                yes={() => (
                                    <div className="spacer-bottom">
                                        <NotificationInline
                                            allowClose={false}
                                            loading={loadingData}
                                            show={!loading}
                                            text={signOffNotification()}
                                            intent={signOffRequired ? "warning" : "success"}
                                        ></NotificationInline>
                                        <div className="button-row spacer-bottom">
                                            <Tooltip content={"Sign off all checks"}>
                                                    <Button
                                                        intent="primary"
                                                        text="SIGN OFF ALL CHECKS"
                                                        id="button-sign-off-all-checks"
                                                        loading={loadingData}
                                                        onClick={() => setShowDefectSignoffModal(true)}
                                                        icon="tick"
                                                        large={false}
                                                        disabled={loadingData || loading || hasOutstandingDefects || !signOffRequired}
                                                    />
                                            </Tooltip>
                                            <ShowHide
                                                evaluator={backToSummary}
                                                hide={
                                                    <InstantReportButton
                                                        icon={<i className="fa fa-file-pdf-o"></i>}
                                                        onReportGenerate={onReportGenerateSummaryReport}
                                                        onReportDownloadComplete={onReportDownloadComplete}
                                                        id="tyre-inspection-summary"
                                                        text="Tyre Inspection Report Summary"
                                                        reportId={generatingReportInstanceId}
                                                        buttonDisabled={!!generatingReportInstanceId || loading || loadingData || reportDownloading}
                                                    />
                                                }
                                                show={
                                                    <InstantReportButton
                                                        icon={<i className="fa fa-file-pdf-o"></i>}
                                                        onReportGenerate={onReportGenerateDetailReport}
                                                        onReportDownloadComplete={onReportDownloadComplete}
                                                        id="tyre-inspection"
                                                        text="Tyre Inspection Report"
                                                        reportId={generatingReportInstanceId}
                                                        buttonDisabled={!!generatingReportInstanceId || loading || loadingData || reportDownloading}
                                                    />
                                                }

                                            ></ShowHide>
 
                                            <ShowHide
                                                evaluator={backToSummary}
                                                show={
                                                    <Tooltip content={"Back to overview"}>
                                                        <div>
                                                            <Button
                                                                intent="primary"
                                                                text="Back to Overview"
                                                                id="button-to-overview"
                                                                loading={loadingData}
                                                                onClick={() => hideOrder()}
                                                                large={false}

                                                            />
                                                        </div>
                                                    </Tooltip>
                                                }
                                            ></ShowHide>
                                        </div>
                                        <NotificationInline
                                            allowClose={false}
                                            show={reportDownloading}
                                            text={"Your report is being generated and will download automatically once ready"}
                                            intent={"primary"}
                                        ></NotificationInline>
                                    </div>
                                )}
                            />
                            <ShowHide
                                evaluator={!loading}
                                show={
                                    <div>
                                        <ShowHide
                                            evaluator={showDetailsListing}
                                            show={
                                                <div>
                                                    <DetailsListing
                                                        onClick={showOrder}
                                                    />
                                                </div>
                                            }
                                        ></ShowHide>
                                        <ShowHide
                                            evaluator={!!showOrderId}
                                            show={
                                                <div>
                                                    <IndividualTyreCheck
                                                        orderId={showOrderId}
                                                    />
                                                </div>
                                            }
                                        ></ShowHide>
                                    </div>
                                }
                            ></ShowHide>
                            <TyreCheckSignOffModal
                                isOpen={showDefectSignoffModal}
                                onClose={() => setShowDefectSignoffModal(false)}
                                onSignOff={() => signOffAllDefects()}
                            />
                        </div>
                    }
                />
            )}
        />
    );
}
